/**
 * Client-side helper to discover which OAuth providers the backend has
 * enabled. Driven by backend env vars exposed via /api/auth-providers
 * so prod can flip providers on/off without rebuilding the frontend.
 *
 * Response is stable for the session — cached at module scope so
 * repeated mounts (login pages, modals) don't refetch.
 *
 * Unknown keys default to `false`. New providers can be added on the
 * backend without touching old clients.
 */

export type AuthProviders = {
  google: boolean;
  apple: boolean;
  mockOauth: boolean;
};

const DEFAULT_PROVIDERS: AuthProviders = {
  google: false,
  apple: false,
  mockOauth: false,
};

let cached: AuthProviders | null = null;
let inflight: Promise<AuthProviders> | null = null;

export async function fetchAuthProviders(): Promise<AuthProviders> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch("/api/auth-providers", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        cached = DEFAULT_PROVIDERS;
        return cached;
      }
      const json = await res.json();
      const data = (json?.data ?? {}) as Partial<AuthProviders>;
      cached = {
        google: data.google === true,
        apple: data.apple === true,
        mockOauth: data.mockOauth === true,
      };
      return cached;
    } catch {
      cached = DEFAULT_PROVIDERS;
      return cached;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Test-only. Clears the module-level cache. */
export function __resetAuthProvidersCacheForTests() {
  cached = null;
  inflight = null;
}
